import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SiaPwaInstallInstructionsDialogComponent } from '../dialogs/sia-pwa-install-instructions-dialog/sia-pwa-install-instructions-dialog.component';
import { notify } from './notify';
import { RuntimePwaApp, getRuntimeConfig } from './runtime-config';

const STORAGE_KEY = 'sia.pwa.selectedAppId';
const PENDING_DIALOG_KEY = 'sia.pwa.pendingInstallDialog';
const PROMPT_WAIT_TIMEOUT_MS = 10_000;

export type SiaPwaInstallOutcome = 'prompted' | 'ios-instructions' | 'generic-instructions' | 'timeout-instructions';

@Injectable({ providedIn: 'root' })
export class SiaPwaInstallService {
  #dialog = inject(MatDialog);

  /** Lista app PWA disponibili (da runtime-config). */
  readonly apps = signal<RuntimePwaApp[]>([]);

  /** Id dell'app selezionata (persistito in localStorage). */
  readonly selectedAppId = signal<string | null>(null);

  /** Deferred prompt nativo (`beforeinstallprompt`). */
  readonly deferredPrompt = signal<any>(null);

  /**
   * True quando, dopo uno swap di manifest, stiamo aspettando che Chrome
   * ri-emetta `beforeinstallprompt` per l'app appena selezionata.
   * Il template usa questo segnale per mostrare un overlay di blocco.
   */
  readonly isWaitingForPrompt = signal<boolean>(false);

  /**
   * True quando, su iOS, abbiamo deciso di ricaricare la pagina per applicare
   * lo swap del manifest a livello di parser HTML. Il template usa questo
   * segnale per mostrare un overlay "preparazione installazione" finche' la
   * navigazione non avviene.
   */
  readonly isReloading = signal<boolean>(false);

  /** True se siamo su iOS Safari (incluso iPadOS che si traveste da Mac). */
  readonly isIOS = computed(() => this.#detectIOS());

  /**
   * True quando il flusso di installazione e' realmente attivabile:
   * - su iOS, sempre (il flusso passa per il dialog istruzioni);
   * - se abbiamo un `deferredPrompt` catturato;
   * - se siamo in attesa di un prompt (il bottone resta disabilitato ma il
   *   signal viene comunque considerato "in transito" per evitare flicker).
   *
   * Il template usa questo signal per il `[disabled]` reattivo dei bottoni:
   * non appena `setDeferredPrompt` viene chiamato con un evento valido, il
   * computed ricalcola e i bottoni si abilitano da soli, senza interazione.
   */
  readonly canInstall = computed(() => this.isIOS() || !!this.deferredPrompt() || this.isWaitingForPrompt());

  /** App correntemente selezionata, derivata da id. */
  readonly selectedApp = computed(() => {
    const id = this.selectedAppId();
    if (!id) return null;
    return this.apps().find((a) => a.id === id) ?? null;
  });

  /** URL del manifest attualmente "live" nel <head>. */
  #activeManifestUrl: string | null = null;
  /** Resolver della Promise in attesa del prossimo `beforeinstallprompt`. */
  #pendingPromptResolver: ((prompt: any | null) => void) | null = null;
  /** Timer associato all'attesa, da cancellare quando il prompt arriva. */
  #pendingPromptTimer: any = null;

  constructor() {
    // Inizializzazione apps dalla runtime-config
    const cfg = getRuntimeConfig();
    if (cfg.pwaApps && cfg.pwaApps.length > 0) {
      this.apps.set(cfg.pwaApps);
    }

    // Restore selezione da localStorage se ancora valida
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored && this.apps().some((a) => a.id === stored)) {
        this.selectedAppId.set(stored);
      }
    } catch {
      // localStorage non disponibile
    }

    // Effect: ogni volta che cambia la selezione, swap identita' PWA + persisti
    effect(() => {
      const app = this.selectedApp();
      if (app) {
        this.#swapPwaIdentity(app);
        try {
          window.localStorage.setItem(STORAGE_KEY, app.id);
        } catch {
          // ignore
        }
      }
    });

    // Riapertura automatica del dialog istruzioni iOS dopo il reload
    // forzato da `#triggerIOSReload`. La chiave viene salvata in sessionStorage
    // pre-reload e consumata qui.
    try {
      const pending = window.sessionStorage.getItem(PENDING_DIALOG_KEY);
      if (pending) {
        window.sessionStorage.removeItem(PENDING_DIALOG_KEY);
        // Posticipiamo l'apertura: MatDialog potrebbe non essere ancora pronto
        // al momento della costruzione del service.
        setTimeout(() => {
          const app = this.apps().find((a) => a.id === pending);
          if (app) {
            this.#openInstructionsDialog(app, 'ios');
          }
        }, 0);
      }
    } catch {
      // sessionStorage non disponibile
    }
  }

  /** True se esiste almeno una app PWA configurata. */
  hasApps(): boolean {
    return this.apps().length > 0;
  }

  /** True se l'app sta girando in standalone (gia installata). */
  isRunningStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches;
  }

  /**
   * Imposta il deferred prompt catturato altrove (es. da `SiaPwaService.install$`).
   * Comportamento listener-persistente: ogni nuova emissione sostituisce il valore
   * precedente. Se c'e' una `triggerInstall` in attesa del prossimo prompt, la
   * risolve qui.
   */
  setDeferredPrompt(prompt: any) {
    const next = prompt ?? null;
    this.deferredPrompt.set(next);
    if (next && this.#pendingPromptResolver) {
      const resolve = this.#pendingPromptResolver;
      this.#pendingPromptResolver = null;
      if (this.#pendingPromptTimer) {
        clearTimeout(this.#pendingPromptTimer);
        this.#pendingPromptTimer = null;
      }
      resolve(next);
    }
  }

  /** Seleziona l'app per id; lo swap del manifest avviene via effect. */
  selectApp(id: string) {
    if (this.apps().some((a) => a.id === id)) {
      this.selectedAppId.set(id);
    }
  }

  /**
   * Avvia il flusso di installazione per l'app data:
   * - iOS Safari → apre dialog con istruzioni iOS.
   * - Android/desktop, manifest invariato → invoca direttamente il prompt corrente.
   * - Android/desktop, manifest cambiato → scarta il prompt corrente, attende il
   *   nuovo `beforeinstallprompt` (max 10s). Se arriva, invoca `prompt()`. Altrimenti
   *   apre dialog generico con messaggio di timeout.
   */
  async triggerInstall(app: RuntimePwaApp): Promise<SiaPwaInstallOutcome> {
    // iOS non emette mai `beforeinstallprompt`. Inoltre Safari iOS legge il
    // <link rel="manifest"> solo al parse iniziale: cambiarlo via JS dopo il
    // bootstrap non basta a far vedere la PWA corretta in "Aggiungi a Home".
    // Quindi se il manifest sta cambiando forziamo un reload — il pre-boot
    // hook in `_endpoints.js` re-inietta i tag corretti prima del parse.
    if (this.isIOS()) {
      const manifestChangingIOS = this.#activeManifestUrl !== app.manifestUrl;
      if (manifestChangingIOS) {
        this.#triggerIOSReload(app);
        // Il return non viene davvero consumato: la pagina si ricarica.
        return 'ios-instructions';
      }
      this.selectApp(app.id);
      this.#openInstructionsDialog(app, 'ios');
      return 'ios-instructions';
    }

    // Calcoliamo se il manifest sta davvero cambiando PRIMA di toccare il <head>.
    const manifestChanging = this.#activeManifestUrl !== app.manifestUrl;

    this.selectApp(app.id);
    const swapped = this.#swapPwaIdentity(app);
    try {
      window.localStorage.setItem(STORAGE_KEY, app.id);
    } catch {
      // ignore
    }

    // Se il manifest non e' cambiato (e nemmeno lo swap fisico ha fatto nulla),
    // il prompt corrente e' coerente con l'app selezionata: usalo direttamente.
    if (!manifestChanging && !swapped) {
      const prompt = this.deferredPrompt();
      if (prompt) {
        try {
          prompt.prompt();
          await prompt.userChoice;
        } catch {
          // ignore
        } finally {
          this.deferredPrompt.set(null);
        }
        return 'prompted';
      }
      // Nessun prompt ancora catturato: invece di mostrare subito il dialog
      // generico, attendiamo che `beforeinstallprompt` arrivi (max N secondi).
      // Se arriva, lo invochiamo. Se scade il timeout, mostriamo il fallback
      // istruzioni e una notifica esplicita all'utente: il bottone, non
      // essendo piu' in `isWaitingForPrompt`, torna abilitato automaticamente.
      this.isWaitingForPrompt.set(true);
      try {
        const newPrompt = await this.#waitForNextPrompt(PROMPT_WAIT_TIMEOUT_MS);
        if (!newPrompt) {
          this.#openInstructionsDialog(app, 'timeout');
          notify('Impossibile installare l app, riprova fra qualche istante.', 4000, 'error');
          return 'timeout-instructions';
        }
        try {
          newPrompt.prompt();
          await newPrompt.userChoice;
        } catch {
          // ignore — l'utente puo' chiudere il prompt nativo
        }
        return 'prompted';
      } finally {
        this.isWaitingForPrompt.set(false);
        this.deferredPrompt.set(null);
      }
    }

    // Manifest cambiato: il prompt corrente (se c'era) si riferisce al manifest
    // precedente. Lo scartiamo e attendiamo il nuovo `beforeinstallprompt`.
    this.deferredPrompt.set(null);
    this.isWaitingForPrompt.set(true);
    try {
      const newPrompt = await this.#waitForNextPrompt(PROMPT_WAIT_TIMEOUT_MS);
      if (!newPrompt) {
        this.#openInstructionsDialog(app, 'timeout');
        notify('Impossibile installare l app, riprova fra qualche istante.', 4000, 'error');
        return 'timeout-instructions';
      }
      try {
        newPrompt.prompt();
        await newPrompt.userChoice;
      } catch {
        // ignore — l'utente puo' chiudere il prompt nativo
      }
      return 'prompted';
    } finally {
      this.isWaitingForPrompt.set(false);
      this.deferredPrompt.set(null);
    }
  }

  /**
   * Ritorna una Promise che si risolve col prossimo `beforeinstallprompt`
   * passato a `setDeferredPrompt`, oppure con `null` allo scadere del timeout.
   * Solo una attesa per volta: se chiamata mentre un'altra e' attiva, quella
   * precedente viene risolta a `null` e annullata.
   */
  #waitForNextPrompt(timeoutMs: number): Promise<any | null> {
    // Annulla eventuale attesa in corso.
    if (this.#pendingPromptResolver) {
      const stale = this.#pendingPromptResolver;
      this.#pendingPromptResolver = null;
      stale(null);
    }
    if (this.#pendingPromptTimer) {
      clearTimeout(this.#pendingPromptTimer);
      this.#pendingPromptTimer = null;
    }

    return new Promise<any | null>((resolve) => {
      this.#pendingPromptResolver = resolve;
      this.#pendingPromptTimer = setTimeout(() => {
        if (this.#pendingPromptResolver === resolve) {
          this.#pendingPromptResolver = null;
        }
        this.#pendingPromptTimer = null;
        resolve(null);
      }, timeoutMs);
    });
  }

  /**
   * Su iOS Safari, quando il manifest sta cambiando, l'unico modo per far
   * vedere la PWA corretta in "Aggiungi a Home" e' ricaricare la pagina dopo
   * aver persistito la selezione: il pre-boot hook in `_endpoints.js` legge
   * `localStorage` e re-inietta i tag corretti prima del parse del manifest
   * statico. Salviamo anche un flag in `sessionStorage` cosi' al boot
   * successivo riapriamo automaticamente il dialog istruzioni iOS.
   */
  #triggerIOSReload(app: RuntimePwaApp) {
    try {
      window.sessionStorage.setItem(PENDING_DIALOG_KEY, app.id);
    } catch {
      // sessionStorage non disponibile: il dialog non si riaprira' da solo
      // ma il reload + manifest swap funzioneranno comunque.
    }
    try {
      // Esplicito: l'effect potrebbe non aver ancora propagato la selezione.
      window.localStorage.setItem(STORAGE_KEY, app.id);
    } catch {
      // ignore
    }
    this.selectedAppId.set(app.id);
    this.isReloading.set(true);
    window.location.reload();
  }

  #openInstructionsDialog(app: RuntimePwaApp, mode: 'ios' | 'generic' | 'timeout') {
    this.#dialog.open(SiaPwaInstallInstructionsDialogComponent, {
      data: { app, mode },
      width: '420px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  /**
   * Inietta/rimpiazza nel <head> i tag che identificano la PWA selezionata:
   * - `<link rel="manifest">` (URL del manifest dell'app)
   * - `<link rel="apple-touch-icon">` (icona iOS, da `appleTouchIcon` con fallback su `icon`)
   * - `<meta name="apple-mobile-web-app-title">` (titolo mostrato sotto l'icona iOS, derivato da `name`)
   *
   * Ritorna `true` se ha effettivamente cambiato il manifest live,
   * `false` se l'URL del manifest era gia' attivo. Il valore di ritorno
   * tiene conto solo del manifest (gli altri tag vengono comunque allineati)
   * perche' e' il manifest a determinare se Chrome ri-emettera' `beforeinstallprompt`.
   */
  #swapPwaIdentity(app: RuntimePwaApp): boolean {
    const head = document.head;
    if (!head) return false;

    // Se il pre-boot hook ha gia' iniettato il manifest corretto, sincronizziamo
    // `#activeManifestUrl` leggendo l'href del manifest live nel DOM, in modo che
    // il calcolo di `manifestChanging` sia coerente al primo `triggerInstall`
    // post-reload.
    if (this.#activeManifestUrl === null && (window as any).__siaBootManifestSwapped === true) {
      const liveManifest = head.querySelector('link[rel="manifest"]');
      const liveHref = liveManifest?.getAttribute('href') ?? null;
      if (liveHref) this.#activeManifestUrl = liveHref;
    }

    // 1) <link rel="manifest"> — unico tag, garantito coerente con app.manifestUrl
    const existingManifests = head.querySelectorAll('link[rel="manifest"]');
    let manifestAlreadyActive = false;
    existingManifests.forEach((link) => {
      const href = (link as HTMLLinkElement).getAttribute('href');
      if (href === app.manifestUrl && !manifestAlreadyActive) {
        manifestAlreadyActive = true;
      } else {
        link.parentNode?.removeChild(link);
      }
    });
    if (!manifestAlreadyActive) {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = app.manifestUrl;
      head.appendChild(link);
    }
    this.#activeManifestUrl = app.manifestUrl;

    // 2) <link rel="apple-touch-icon"> — icona iOS
    const appleIconHref = app.appleTouchIcon ?? app.icon;
    if (appleIconHref) {
      const existingAppleIcons = head.querySelectorAll('link[rel="apple-touch-icon"]');
      let appleIconNode: HTMLLinkElement | null = null;
      existingAppleIcons.forEach((link, idx) => {
        if (idx === 0) {
          appleIconNode = link as HTMLLinkElement;
        } else {
          link.parentNode?.removeChild(link);
        }
      });
      if (!appleIconNode) {
        appleIconNode = document.createElement('link');
        appleIconNode.rel = 'apple-touch-icon';
        head.appendChild(appleIconNode);
      }
      if (appleIconNode.getAttribute('href') !== appleIconHref) {
        appleIconNode.setAttribute('href', appleIconHref);
      }
    }

    // 3) <meta name="apple-mobile-web-app-title"> — titolo iOS derivato da app.name
    const existingTitles = head.querySelectorAll('meta[name="apple-mobile-web-app-title"]');
    let appleTitleNode: HTMLMetaElement | null = null;
    existingTitles.forEach((meta, idx) => {
      if (idx === 0) {
        appleTitleNode = meta as HTMLMetaElement;
      } else {
        meta.parentNode?.removeChild(meta);
      }
    });
    if (!appleTitleNode) {
      appleTitleNode = document.createElement('meta');
      appleTitleNode.setAttribute('name', 'apple-mobile-web-app-title');
      head.appendChild(appleTitleNode);
    }
    if (appleTitleNode.getAttribute('content') !== app.name) {
      appleTitleNode.setAttribute('content', app.name);
    }

    return !manifestAlreadyActive;
  }

  #detectIOS(): boolean {
    const ua = navigator.userAgent || '';
    const isIPhoneOrIPad = /iPad|iPhone|iPod/.test(ua);
    // iPadOS 13+ si presenta come Mac con maxTouchPoints > 1
    const isIPadOS =
      /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
    if (!isIPhoneOrIPad && !isIPadOS) return false;
    // Esclude Chrome iOS / Firefox iOS / Edge iOS — non possono installare PWA via prompt nativo
    // ma le istruzioni "Aggiungi a Home" funzionano solo su Safari. Mostriamo comunque il dialog iOS.
    return true;
  }
}
